import React, { useState, useEffect } from 'react';

// App-wide replacement for window.alert()/window.confirm() (Batch 3, ask #5 —
// "pop-ups freeze the page and are worse in the native app" — a native
// WebView's window.alert/confirm either doesn't render at all or blocks the
// JS thread in a way that can hang a Capacitor app). One host, mounted once
// at the app root (App.js) — appAlert()/appConfirm() are plain functions
// callable from anywhere (event handlers, async helpers, utils files with no
// React context), same call-from-anywhere ergonomics as the native versions,
// via a module-level "show" ref the host registers on mount.
//
// appAlert(message, opts?) -> Promise<void>, resolves on dismiss.
// appConfirm(message, opts?) -> Promise<boolean>, resolves true/false —
//   SAME truthiness contract as window.confirm(), but async: every call site
//   written as `if (!confirm(x)) return;` becomes `if (!(await appConfirm(x))) return;`
//   (and its enclosing function must be async — window.confirm blocked the
//   thread so callers never had to be).
// opts: { title, confirmLabel, cancelLabel, danger }

let _show = null;

export function appAlert(message, opts = {}) {
  return new Promise(resolve => {
    if (!_show) { window.alert(message); resolve(); return; } // host not mounted yet — fail open, never silently swallow
    _show({ kind: 'alert', message, title: opts.title || '', onResolve: () => resolve() });
  });
}

export function appConfirm(message, opts = {}) {
  return new Promise(resolve => {
    if (!_show) { resolve(window.confirm(message)); return; }
    _show({
      kind: 'confirm', message, title: opts.title || '',
      confirmLabel: opts.confirmLabel || 'OK', cancelLabel: opts.cancelLabel || 'Cancel', danger: !!opts.danger,
      onResolve: (result) => resolve(result),
    });
  });
}

export function AppAlertHost() {
  const [dialog, setDialog] = useState(null);
  useEffect(() => {
    _show = (d) => setDialog(d);
    return () => { _show = null; };
  }, []);

  if (!dialog) return null;

  function close(result) {
    dialog.onResolve(result);
    setDialog(null);
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onMouseDown={e => { if (e.target === e.currentTarget && dialog.kind === 'alert') close(); }}
    >
      <div style={{ background: 'var(--dark, #1a1d2e)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10, padding: '20px 22px', maxWidth: 420, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
        {dialog.title && <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: '#fff' }}>{dialog.title}</div>}
        <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{dialog.message}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          {dialog.kind === 'confirm' && (
            <button className="btn btn-ghost" onClick={() => close(false)}>{dialog.cancelLabel}</button>
          )}
          <button
            className="btn btn-primary"
            style={dialog.danger ? { background: '#e74c3c', borderColor: '#e74c3c' } : undefined}
            onClick={() => close(dialog.kind === 'confirm' ? true : undefined)}
            autoFocus
          >
            {dialog.kind === 'confirm' ? dialog.confirmLabel : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}
